import { useState, useContext, createContext, Children, useMemo } from 'react';
/** @jsxRuntime classic */

/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import { CaretDownIcon, CaretRightIcon } from '../icon';
import { migratoryWebCitizen, sanitarianRegistersWeb, tdCarWeb } from './Colors';
const themes = {
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.geyser,
    beta: migratoryWebCitizen.valhalla
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.geyser,
    beta: sanitarianRegistersWeb.valhalla
  },
  tdCarWeb: {
    alpha: tdCarWeb.geyser,
    beta: tdCarWeb.valhalla
  }
};
const AccordionContext = /*#__PURE__*/createContext();
/**
 * This component represents an accordion.
 * @param {*} children Accordion content.
 * @param {string} title Accordion title.
 * @param {JSON} titleStyle Custom style for the title.
 * @param {string} iconName Icon name for the acordion, default "caret-right".
 * @param {JSON} iconStyle Custom style for the icon.
 */

const Collapsible = ({
  children,
  title,
  titleStyle,
  iconName = 'caret-right',
  iconStyle,
  theme = 'migratoryWebCitizen'
}) => {
  const {
    open,
    state
  } = useAccordion();
  const {
    isOpen,
    current
  } = state;
  const name = `collapsible-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return jsx("div", {
    name: name,
    css: style(theme)
  }, jsx("div", {
    role: "presentation",
    className: "collapsible-div",
    onClick: () => open(title),
    disabled: true
  }, jsx("h1", {
    className: "collapsible-title",
    style: { ...titleStyle
    }
  }, title), iconName === 'caret-right' ? jsx(CaretRightIcon, {
    className: `icon-style ${isOpen && current === title ? 'icon-rotate' : ''}`,
    css: { ...iconStyle,
      color: (iconStyle === null || iconStyle === void 0 ? void 0 : iconStyle.color) || themes[theme].alpha
    }
  }) : jsx(CaretDownIcon, {
    className: `icon-style ${isOpen && current === title ? 'icon-rotate' : ''}`,
    css: { ...iconStyle,
      color: (iconStyle === null || iconStyle === void 0 ? void 0 : iconStyle.color) || themes[theme].alpha
    }
  })), jsx("div", {
    style: {
      display: `${isOpen && current === title ? 'flex' : 'none'}`
    }
  }, children));
};
/**
 * This component represents is the accordion provider that wraps the accordion.
 * @param {*} children Accordion.
 * @param {JSON} containerStyle Custom style for the accordion wrapper.
 */


const AccordionProvider = ({
  children,
  containerStyle
}) => {
  const memoizedCollapsibles = useMemo(() => {
    const collapsibles = Children.toArray(children).map((child, index) => ({
      id: index,
      order: index,
      name: child.props.title
    }));
    return collapsibles;
  }, []);
  const [state, setState] = useState({
    isOpen: false,
    current: '',
    currentIndex: 0,
    nextIndex: 1,
    collapsibles: memoizedCollapsibles
  });

  const triggerScroll = current => {
    setTimeout(() => {
      const key = `collapsible-${current.replace(/\s+/g, '-').toLowerCase()}`;
      const elmnt = document.querySelector(`[name="${key}"]`);
      elmnt.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };

  const open = current => {
    const currentIndex = state.collapsibles.findIndex(x => x.name === current);
    const nextIndex = currentIndex + 1;
    let doScroll = false;
    const dataToUpdate = {};

    if (!state.current) {
      dataToUpdate.isOpen = !state.isOpen;
      dataToUpdate.current = current;
      doScroll = true;
    } else if (state.current === current) {
      dataToUpdate.isOpen = !state.isOpen;
      dataToUpdate.current = '';
    } else if (state.current !== current) {
      dataToUpdate.current = current;
      doScroll = true;
    }

    setState(values => ({ ...values,
      ...dataToUpdate,
      currentIndex,
      nextIndex
    }));

    if (doScroll) {
      triggerScroll(current);
    }
  };

  const next = () => {
    const {
      nextIndex,
      currentIndex
    } = state;

    if (currentIndex < state.collapsibles.length - 1) {
      setState(values => ({ ...values,
        current: state.collapsibles[nextIndex].name,
        currentIndex: currentIndex + 1,
        nextIndex: nextIndex + 1 > state.collapsibles.length ? nextIndex : nextIndex + 1
      }));
      triggerScroll(state.collapsibles[nextIndex].name);
    }
  };

  return jsx(AccordionContext.Provider, {
    value: {
      open,
      state,
      next
    }
  }, jsx("div", {
    style: { ...containerStyle
    }
  }, children));
};

function useAccordion() {
  const context = useContext(AccordionContext);

  if (context === undefined) {
    throw new Error(`useAccordion must be used within a CollapsibleProvider`);
  }

  return context;
}

export { AccordionProvider, useAccordion, Collapsible };

const style = theme => css`
  .collapsible-div {
    width: 100%;
    position: relative;
    cursor: pointer;
    margin-bottom: 30px;
  }

  .collapsible-title {
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${themes[theme].beta};
    margin: 0;
    display: inline-block;
  }

  .icon-style {
    height: 18px;
    width: 18px;
    position: relative;
    top: 2px;
    margin-left: 15px;
  }

  .icon-rotate {
    transform: rotate(90deg);
  }
`;